/**
 * Sidebar Navigation Component
 * 
 * Full navigation menu with collapsible groups, icons, and active state
 * Responsive and supports mobile drawer mode
 */

import { NavLink } from 'react-router-dom';
import { 
  LayoutDashboard,
  FileText,
  Wrench,
  Users,
  Shield,
  Package,
  FolderOpen,
  Waves,
  Navigation,
  ClipboardCheck,
  FileBarChart,
  Settings,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  X,
  Ship,
  Award,
  AlertTriangle,
  Calendar,
  BookOpen,
  Droplet,
  Compass,
  FileCheck,
  UserCog,
  Search,
  Beaker,
  Biohazard
} from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@/contexts/ThemeContext';

interface SidebarProps {
  collapsed: boolean;
  onToggleCollapse: () => void;
  onClose: () => void;
  isMobile?: boolean;
}

interface NavItem {
  name: string;
  path: string;
  icon: React.ElementType;
  badge?: number;
}

interface NavGroup {
  name: string;
  items: NavItem[];
  icon?: React.ElementType;
}

export default function Sidebar({ collapsed, onToggleCollapse, onClose, isMobile }: SidebarProps) {
  const [expandedGroups, setExpandedGroups] = useState<string[]>(['main']);
  const { isClassic } = useTheme();
  const { t } = useTranslation(['common', 'components']);

  // Feature flags for milestone management
  // Set to false to hide features from navigation
  const FEATURE_FLAGS = {
    logbook: false,        // Hidden for current milestone
    environmental: false,  // Hidden for current milestone
    ihm: false,           // Hidden for current milestone
  };

  const toggleGroup = (groupName: string) => {
    setExpandedGroups(prev =>
      prev.includes(groupName)
        ? prev.filter(g => g !== groupName)
        : [...prev, groupName]
    );
  };

  const navigationGroups: NavGroup[] = [
    {
      name: t('components:sidebar.dashboard'),
      items: [
        { name: t('components:sidebar.dashboard'), path: '/', icon: LayoutDashboard },
      ],
    },
    {
      name: t('components:sidebar.certificates.title'),
      icon: Award,
      items: [
        { name: t('components:sidebar.certificates.all'), path: '/certificates', icon: FileText },
        { name: t('components:sidebar.certificates.expiring'), path: '/certificates/expiring', icon: AlertTriangle, badge: 3 },
      ],
    },
    {
      name: t('components:sidebar.maintenance.title'),
      icon: Wrench,
      items: [
        { name: t('components:sidebar.maintenance.overview'), path: '/maintenance', icon: Wrench },
        { name: t('components:sidebar.maintenance.equipment'), path: '/maintenance/equipment', icon: Wrench },
        { name: t('components:sidebar.maintenance.workOrders'), path: '/maintenance/work-orders', icon: ClipboardCheck },
        { name: t('components:sidebar.maintenance.calendar'), path: '/maintenance/calendar', icon: Calendar },
      ],
    },
    {
      name: t('components:sidebar.crew.title'),
      icon: Users,
      items: [
        { name: t('components:sidebar.crew.personnel'), path: '/crew/personnel', icon: Users },
        { name: t('components:sidebar.crew.certificates'), path: '/crew/certificates', icon: Award },
        { name: t('components:sidebar.crew.workingHours'), path: '/crew/hours', icon: Calendar },
        { name: t('components:sidebar.crew.training'), path: '/crew/training', icon: BookOpen },
      ],
    },
    {
      name: t('components:sidebar.safety.title'),
      icon: Shield,
      items: [
        { name: t('components:sidebar.safety.inspections'), path: '/safety/inspections', icon: ClipboardCheck },
        { name: t('components:sidebar.safety.incidents'), path: '/safety/incidents', icon: AlertTriangle },
        { name: t('components:sidebar.safety.drills'), path: '/safety/drills', icon: Shield },
        { name: t('components:sidebar.safety.riskAssessments'), path: '/safety/risk-assessments', icon: FileCheck },
      ],
    },
    {
      name: t('components:sidebar.inventory.title'),
      icon: Package,
      items: [
        { name: t('components:sidebar.inventory.stockItems'), path: '/inventory', icon: Package },
        { name: t('components:sidebar.inventory.transactions'), path: '/inventory/transactions', icon: FileBarChart },
        { name: t('components:sidebar.inventory.requisitions'), path: '/inventory/requisitions', icon: ClipboardCheck },
      ],
    },
    {
      name: t('components:sidebar.documents.title'),
      items: [
        { name: t('components:sidebar.documents.all'), path: '/documents', icon: FolderOpen },
      ],
    },
    {
      name: t('components:sidebar.forms.title'),
      icon: FileText,
      items: [
        { name: t('components:sidebar.forms.library'), path: '/forms', icon: FileText },
        { name: t('components:sidebar.forms.submissions'), path: '/forms/submissions', icon: ClipboardCheck },
        { name: t('components:sidebar.forms.missing'), path: '/forms/missing', icon: AlertTriangle },
      ],
    },
    // Logbook - Hidden for current milestone
    ...(FEATURE_FLAGS.logbook ? [{
      name: t('components:sidebar.logbook.title'),
      icon: BookOpen,
      items: [
        { name: t('components:sidebar.logbook.dashboard'), path: '/logbook', icon: LayoutDashboard },
        { name: t('components:sidebar.logbook.entries'), path: '/logbook/entries', icon: FileText },
        { name: t('components:sidebar.logbook.reports'), path: '/logbook/reports', icon: FileBarChart },
        { name: t('components:sidebar.logbook.watchSchedule'), path: '/logbook/watch-schedule', icon: Calendar },
      ],
    }] : []),
    // Environmental - Hidden for current milestone
    ...(FEATURE_FLAGS.environmental ? [{
      name: t('components:sidebar.environmental.title'),
      icon: Waves,
      items: [
        { name: t('components:sidebar.environmental.orb'), path: '/environmental/orb', icon: Droplet },
        { name: t('components:sidebar.environmental.grb'), path: '/environmental/grb', icon: Package },
        { name: t('components:sidebar.environmental.ballast'), path: '/environmental/ballast', icon: Waves },
        { name: t('components:sidebar.environmental.emissions'), path: '/environmental/emissions', icon: Waves },
      ],
    }] : []),
    // IHM - Hidden for current milestone
    ...(FEATURE_FLAGS.ihm ? [{
      name: t('components:sidebar.ihm.title'),
      icon: Beaker,
      items: [
        { name: t('components:sidebar.ihm.dashboard'), path: '/ihm', icon: LayoutDashboard },
        { name: t('components:sidebar.ihm.materials'), path: '/ihm/materials', icon: Biohazard },
        { name: t('components:sidebar.ihm.sds'), path: '/ihm/sds', icon: FileText },
        { name: t('components:sidebar.ihm.inspections'), path: '/ihm/inspections', icon: ClipboardCheck },
        { name: t('components:sidebar.ihm.removalPlan'), path: '/ihm/removal-plan', icon: AlertTriangle },
      ],
    }] : []),
    {
      name: t('components:sidebar.navigation.title'),
      icon: Navigation,
      items: [
        { name: t('components:sidebar.navigation.positions'), path: '/navigation/positions', icon: Compass },
        { name: t('components:sidebar.navigation.passages'), path: '/navigation/passages', icon: Navigation },
        { name: t('components:sidebar.navigation.portCalls'), path: '/navigation/port-calls', icon: Ship },
      ],
    },
    {
      name: t('components:sidebar.compliance.title'),
      icon: ClipboardCheck,
      items: [
        { name: t('components:sidebar.compliance.audits'), path: '/compliance/audits', icon: ClipboardCheck },
        { name: t('components:sidebar.compliance.nonConformities'), path: '/compliance/nc', icon: AlertTriangle },
      ],
    },
    {
      name: t('components:sidebar.correctiveActions.title'),
      icon: AlertTriangle,
      items: [
        { name: t('components:sidebar.correctiveActions.all'), path: '/corrective-actions', icon: AlertTriangle },
      ],
    },
    {
      name: t('components:sidebar.tasks.title'),
      icon: ClipboardCheck,
      items: [
        { name: t('components:sidebar.tasks.dashboard'), path: '/tasks', icon: ClipboardCheck },
      ],
    },
    {
      name: 'system',
      items: [
        { name: t('common:actions.search'), path: '/search', icon: Search },
        { name: t('components:sidebar.reports.title'), path: '/reports', icon: FileBarChart },
        { name: t('components:sidebar.users.title'), path: '/users', icon: UserCog },
        { name: t('components:sidebar.settings.title'), path: '/settings', icon: Settings },
      ],
    },
  ];

  if (isClassic) {
    // Classic Tree View Layout
    return (
      <div className="classic-tree-view flex h-full flex-col">
        {/* Navigation as Tree */}
        <nav className="flex-1 overflow-y-auto p-2">
          {navigationGroups.map((group) => (
            <div key={group.name} className="mb-1">
              {/* Group header (tree folder) */}
              <div
                onClick={() => toggleGroup(group.name)}
                className="classic-tree-item font-bold cursor-pointer"
              >
                {expandedGroups.includes(group.name) ? (
                  <ChevronDown className="h-3 w-3 tree-icon" />
                ) : (
                  <ChevronRight className="h-3 w-3 tree-icon" />
                )}
                {group.icon && <group.icon className="h-4 w-4" />}
                <span>{group.name}</span>
              </div>

              {/* Group items (tree children) */}
              {expandedGroups.includes(group.name) && (
                <div className="classic-tree-group">
                  {group.items.map((item) => (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      onClick={isMobile ? onClose : undefined}
                      className={({ isActive }) =>
                        `classic-tree-item ${isActive ? 'selected' : ''}`
                      }
                    >
                      {item.icon && <item.icon className="h-4 w-4" />}
                      <span>{item.name}</span>
                      {item.badge && (
                        <span className="ml-auto inline-flex items-center justify-center rounded bg-red-600 px-1.5 py-0.5 text-[10px] font-bold text-white">
                          {item.badge}
                        </span>
                      )}
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
          ))}
        </nav>
      </div>
    );
  }

  // Modern Sidebar Layout
  return (
    <div className="flex h-full flex-col bg-gray-900 text-white">
      {/* Header */}
      <div className="flex h-16 items-center justify-between px-4 border-b border-gray-800">
        {!collapsed && (
          <div className="flex items-center space-x-3">
            <Ship className="h-8 w-8 text-blue-400" />
            <span className="text-xl font-bold">MARS-X</span>
          </div>
        )}
        
        {collapsed && !isMobile && (
          <Ship className="h-8 w-8 text-blue-400 mx-auto" />
        )}

        {/* Mobile close button */}
        {isMobile && (
          <button
            onClick={onClose}
            className="rounded-md p-2 hover:bg-gray-800 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        )}

        {/* Desktop collapse button */}
        {!isMobile && (
          <button
            onClick={onToggleCollapse}
            className="rounded-md p-2 hover:bg-gray-800 transition-colors"
          >
            {collapsed ? (
              <ChevronRight className="h-5 w-5" />
            ) : (
              <ChevronLeft className="h-5 w-5" />
            )}
          </button>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-4">
        {navigationGroups.map((group) => (
          <div key={group.name} className="mb-2">
            {/* Group header (collapsible) */}
            {group.items.length > 1 && !collapsed && (
              <button
                onClick={() => toggleGroup(group.name)}
                className="flex w-full items-center justify-between px-4 py-2 text-xs font-semibold uppercase tracking-wider text-gray-400 hover:text-white transition-colors"
              >
                <span className="flex items-center space-x-2">
                  {group.icon && <group.icon className="h-4 w-4" />}
                  <span>{group.name}</span>
                </span>
                <ChevronRight
                  className={`h-4 w-4 transition-transform ${
                    expandedGroups.includes(group.name) ? 'rotate-90' : ''
                  }`}
                />
              </button>
            )}

            {/* Group items */}
            {(expandedGroups.includes(group.name) || collapsed) && (
              <div className="space-y-1">
                {group.items.map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    onClick={isMobile ? onClose : undefined}
                    className={({ isActive }) =>
                      `flex items-center px-4 py-2.5 text-sm font-medium transition-colors ${
                        collapsed ? 'justify-center' : 'justify-between'
                      } ${
                        isActive
                          ? 'bg-blue-600 text-white'
                          : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                      }`
                    }
                    title={collapsed ? item.name : undefined}
                  >
                    <div className="flex items-center space-x-3">
                      <item.icon className="h-5 w-5 flex-shrink-0" />
                      {!collapsed && <span>{item.name}</span>}
                    </div>
                    {!collapsed && item.badge && (
                      <span className="inline-flex items-center justify-center rounded-full bg-red-500 px-2 py-0.5 text-xs font-bold">
                        {item.badge}
                      </span>
                    )}
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        ))}
      </nav>

      {/* Footer */}
      {!collapsed && (
        <div className="border-t border-gray-800 p-4">
          <div className="text-xs text-gray-400">
            <div className="font-semibold">MARS-X</div>
            <div>{t('components:sidebar.version', { version: '0.5.0' })}</div>
          </div>
        </div>
      )}
    </div>
  );
}
